import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Loader2, Plus, Check } from 'lucide-react'
import { companiesApi, campaignsApi } from '@/services/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'

interface AddCompaniesToCampaignDialogProps {
  campaignId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface Company {
  id: string
  name: string
  website?: string
  source?: string
  stage?: string
}

export function AddCompaniesToCampaignDialog({
  campaignId,
  open,
  onOpenChange,
}: AddCompaniesToCampaignDialogProps) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const { data: companiesData, isLoading } = useQuery({
    queryKey: ['companies-for-campaign', search],
    queryFn: () =>
      companiesApi.list({
        page: 1,
        per_page: 50,
        search: search || undefined,
        stage: 'qualified',
      }),
    enabled: open,
  })

  const addMutation = useMutation({
    mutationFn: (companyIds: string[]) =>
      campaignsApi.addCompanies(campaignId, companyIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
      queryClient.invalidateQueries({
        queryKey: ['campaign-companies', campaignId],
      })
      setSelectedIds(new Set())
      onOpenChange(false)
    },
  })

  const companies: Company[] = companiesData?.data ?? []

  const toggleCompany = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  const handleAdd = () => {
    if (selectedIds.size > 0) {
      addMutation.mutate(Array.from(selectedIds))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Companies to Campaign</DialogTitle>
          <DialogDescription>
            Select qualified companies to add to this campaign for outreach.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <ScrollArea className="h-[300px] rounded-md border">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : companies.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No qualified companies found
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {companies.map((company) => (
                  <div
                    key={company.id}
                    className="flex items-center gap-3 rounded-md p-2 hover:bg-muted cursor-pointer"
                    onClick={() => toggleCompany(company.id)}
                  >
                    <Checkbox
                      checked={selectedIds.has(company.id)}
                      onCheckedChange={() => toggleCompany(company.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{company.name}</div>
                      {company.website && (
                        <div className="text-xs text-muted-foreground truncate">
                          {company.website}
                        </div>
                      )}
                    </div>
                    {company.source && (
                      <Badge variant="outline" className="text-xs">
                        {company.source}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4" />
              {selectedIds.size} compan{selectedIds.size === 1 ? 'y' : 'ies'}{' '}
              selected
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selectedIds.size === 0 || addMutation.isPending}
          >
            {addMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add {selectedIds.size > 0 ? selectedIds.size : ''} Companies
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
